import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Room } from '../types'
import type { SendFn } from '../App'
import SuggestIdeasModal from './SuggestIdeasModal'
import ConfirmDialog from './ConfirmDialog'
import { GROUP_MODE_ENABLED } from '../../shared/messages'

// Singular forms of the idea-bank categories (server/db/seed.sql). Cycled
// underneath the bowl input as a hint that anything can go in. Keep in
// rough sync with the seed list — if a category is added there and isn't
// shown here it's not a bug, just a missed shoutout.
const BOWL_HINT_NOUNS = [
  'pizza topping',
  'ice cream flavor',
  'breakfast food',
  'sandwich filling',
  'cocktail',
  'snack',
  'comfort food',
  'pasta dish',
  'condiment',
  "'90s movie",
  'sitcom character',
  'music genre',
  'karaoke song',
  'board game',
  'sport to watch',
  'indoor hobby',
  'outdoor activity',
  'pet animal',
  'city to visit',
  'national park',
  'vacation type',
  'Halloween costume',
  'childhood toy',
  'phone app',
  'hot take',
  'first-date dealbreaker',
  'roommate dealbreaker',
  'workplace pet peeve',
  'good smell',
  'tiny pleasure',
]

// "a" vs "an" based on the first spoken letter. Skip non-alpha (apostrophes,
// digits) so "'90s movie" still resolves to its first letter sound. Covers
// every entry in BOWL_HINT_NOUNS — extend if you add tricky ones (silent h,
// "yuh"-sounding u, etc.).
function article(word: string): 'a' | 'an' {
  const first = word.replace(/[^a-zA-Z]/g, '')[0]?.toLowerCase() ?? ''
  return 'aeiou'.includes(first) ? 'an' : 'a'
}

function shuffled<T>(arr: T[]): T[] {
  const out = [...arr]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

interface LobbyProps {
  room: Room
  playerId: string
  playerName: string
  onLeave: () => void
  send: SendFn
}

export default function Lobby({ room, playerId, onLeave, send }: LobbyProps) {
  const [item, setItem] = useState('')
  const [previewIndex, setPreviewIndex] = useState(0)
  const [suggestOpen, setSuggestOpen] = useState(false)
  const [confirmClearOpen, setConfirmClearOpen] = useState(false)
  // Cycle through example category nouns under the bowl input. Shuffled
  // once per mount so two players in the same lobby aren't synchronized.
  const [hintList] = useState(() => shuffled(BOWL_HINT_NOUNS))
  const [hintIndex, setHintIndex] = useState(0)
  const isHost = playerId === room.hostId
  // Active players only — anyone who has left the room (or whose reconnect
  // window expired) is hidden from the lobby list and ignored for start-game.
  const players = Object.values(room.players).filter((p) => !p.departed)
  const canStart = room.bowl.length >= 5 && players.length >= 3

  const addFromSuggestion = (text: string) => {
    const exists = room.bowl.some((b) => b.toLowerCase() === text.toLowerCase())
    if (exists) return
    send('add_to_bowl', { items: [text] })
  }

  useEffect(() => {
    if (room.bowl.length === 0) return
    const interval = setInterval(() => {
      setPreviewIndex((prev) => (prev + 1) % room.bowl.length)
    }, 2500)
    return () => clearInterval(interval)
  }, [room.bowl.length])

  useEffect(() => {
    const interval = setInterval(() => {
      setHintIndex((prev) => (prev + 1) % hintList.length)
    }, 1500)
    return () => clearInterval(interval)
  }, [hintList.length])

  const addItem = () => {
    const trimmed = item.trim()
    if (!trimmed) return
    const exists = room.bowl.some((b) => b.toLowerCase() === trimmed.toLowerCase())
    if (exists) {
      setItem('')
      return
    }
    send('add_to_bowl', { items: [trimmed] })
    setItem('')
  }

  const startGame = () => {
    if (!canStart) return
    send('start_game', {})
  }

  const clearBowl = () => {
    if (!isHost) return
    setConfirmClearOpen(true)
  }

  const confirmClearBowl = () => {
    setConfirmClearOpen(false)
    send('clear_bowl', {})
  }

  const startLabel = !canStart
    ? room.bowl.length < 5
      ? `Need ${5 - room.bowl.length} more items`
      : `Need ${Math.max(0, 3 - players.length)} more player${players.length === 2 ? '' : 's'}`
    : 'Start Game'

  return (
    <div className="flex-1 flex flex-col px-6 py-8 max-w-lg mx-auto w-full">
      {/* Header */}
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', damping: 10 }}
          className="inline-block bg-white rounded-2xl shadow-lg px-8 py-5 mb-4"
        >
          <p className="text-sm font-body text-warmblack/50 mb-1">Room Code</p>
          <p className="font-display text-5xl font-bold tracking-[0.2em] text-coral">
            {room.code}
          </p>
        </motion.div>
        <p className="text-warmblack/60 font-body text-base">Share this code with friends</p>
      </div>

      {/* Players */}
      <div className="mb-6">
        <h2 className="font-display text-xl font-bold mb-3 text-warmblack/70">
          Players ({players.length})
        </h2>
        <div className="flex flex-wrap gap-2">
          <AnimatePresence>
            {players.map((p) => (
              <motion.div
                key={p.id}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                className={`px-5 py-2.5 rounded-full font-body font-bold text-base ${
                  p.id === playerId
                    ? 'bg-coral text-white'
                    : 'bg-white text-warmblack shadow-sm border border-warmblack/5'
                } ${p.connected ? '' : 'opacity-50'}`}
              >
                {p.name} {p.id === room.hostId && '(host)'}{!p.connected && ' …'}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Bowl */}
      <div className="flex-1 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-bold text-warmblack/70">
            The Bowl ({room.bowl.length} items)
          </h2>
          {isHost && room.bowl.length > 0 && (
            <button
              onClick={clearBowl}
              className="font-body text-sm text-warmblack/40 hover:text-coral underline underline-offset-2"
            >
              Clear bowl
            </button>
          )}
        </div>

        {/* Add item input */}
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            placeholder="Add something to the bowl..."
            value={item}
            onChange={(e) => setItem(e.target.value)}
            maxLength={60}
            className="flex-1 font-body text-lg py-4 px-5 rounded-xl border-2 border-warmblack/10 bg-white focus:border-coral focus:outline-none transition-colors"
            onKeyDown={(e) => e.key === 'Enter' && addItem()}
          />
          <button
            onClick={addItem}
            disabled={!item.trim()}
            className="bg-sunflower hover:bg-sunflower/80 disabled:opacity-40 text-warmblack font-display font-bold text-2xl px-6 rounded-xl shadow-md transition-all active:scale-95"
          >
            +
          </button>
        </div>
        <p className="font-body text-sm text-warmblack/55 mb-2 px-1">
          You can add anything — it could be{' '}
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={hintList[hintIndex]}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
              className="inline-block font-bold text-warmblack"
            >
              {article(hintList[hintIndex])} {hintList[hintIndex]}
            </motion.span>
          </AnimatePresence>
        </p>
        <button
          onClick={() => setSuggestOpen(true)}
          className="mb-4 font-body text-sm text-coral hover:underline underline-offset-2 font-bold"
        >
          Need ideas? Browse suggestions →
        </button>

        {/* Rolling preview — one item at a time */}
        {room.bowl.length > 0 && (
          <div className="h-16 flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.span
                key={previewIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="inline-block bg-sunflower/30 text-warmblack px-5 py-2.5 rounded-full font-body text-lg border border-sunflower/50"
              >
                {room.bowl[previewIndex]}
              </motion.span>
            </AnimatePresence>
          </div>
        )}

      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        {/* Game mode — hidden when only one mode exists. */}
        {GROUP_MODE_ENABLED && (
          <div className="flex flex-col items-center gap-2">
            <p className="font-body text-sm text-warmblack/50">Game mode</p>
            {isHost ? (
              <div className="inline-flex bg-white rounded-full p-1 border border-warmblack/10 shadow-sm">
                <button
                  onClick={() => send('set_game_mode', { mode: 'individual' })}
                  className={`px-4 py-2 rounded-full font-body text-sm font-bold transition-colors ${
                    room.gameMode === 'individual'
                      ? 'bg-coral text-white'
                      : 'text-warmblack/60 hover:text-warmblack'
                  }`}
                >
                  Everyone votes
                </button>
                <button
                  onClick={() => send('set_game_mode', { mode: 'group' })}
                  className={`px-4 py-2 rounded-full font-body text-sm font-bold transition-colors ${
                    room.gameMode === 'group'
                      ? 'bg-coral text-white'
                      : 'text-warmblack/60 hover:text-warmblack'
                  }`}
                >
                  Group consensus
                </button>
              </div>
            ) : (
              <p className="font-body text-base font-bold text-warmblack">
                {room.gameMode === 'group' ? 'Group consensus' : 'Everyone votes'}
              </p>
            )}
            <p className="font-body text-sm text-warmblack/50 text-center px-4">
              {room.gameMode === 'group'
                ? 'One scribe submits a single ranking for the whole group. Everyone shares the score.'
                : 'Every player submits their own ranking. Score is per player.'}
            </p>
          </div>
        )}
        {isHost && (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={startGame}
            disabled={!canStart}
            className="bg-coral hover:bg-coral/90 disabled:opacity-40 text-white font-display font-bold text-2xl py-5 rounded-2xl shadow-lg transition-all animate-pulse-glow disabled:animate-none"
          >
            {startLabel}
          </motion.button>
        )}
        {!isHost && (
          <p className="text-center text-warmblack/50 font-body text-base">
            Waiting for host to start the game...
          </p>
        )}
        <button
          onClick={onLeave}
          className="text-warmblack/40 font-body text-base hover:text-warmblack transition-colors"
        >
          Leave room
        </button>
      </div>
      <AnimatePresence>
        {suggestOpen && (
          <SuggestIdeasModal
            bowl={room.bowl}
            onAddItem={addFromSuggestion}
            onClose={() => setSuggestOpen(false)}
          />
        )}
      </AnimatePresence>
      <ConfirmDialog
        open={confirmClearOpen}
        title="Clear the bowl?"
        message="This removes every item players have added so far."
        confirmLabel="Clear bowl"
        onConfirm={confirmClearBowl}
        onCancel={() => setConfirmClearOpen(false)}
      />
    </div>
  )
}
